import { useEffect, useState, useCallback, useRef } from 'react';

import { getNestedValue, scrollIntoFirstInvalidItem, setNestedValue } from '../utilities/helpers';

export function useForm(initialValues, ref = null) {
  const [values, setValues] = useState(initialValues);
  const [validated, setValidated] = useState(false);
  const [validationErrors, setValidationErrors] = useState();

  useEffect(() => {
    return () => setValues(initialValues);
  }, []);

  const formRef = useRef(ref?.current);

  useEffect(() => {
    if (validationErrors) {
      setValidated(false);
      scrollIntoFirstInvalidItem(formRef?.current);
    }
  }, [validationErrors]);

  const focusRef = useCallback((element) => {
    if (element) element.focus();
  }, []);

  const setValue = useCallback((prop, value) => {
    if (!prop.includes('.')) {
      return setValues((prev) => ({ ...prev, [prop]: value }));
    }

    setValues((prev) => {
      if (!prev) {
        prev = {};
      }

      const newState = { ...prev };
      setNestedValue(prop, newState, value);
      return newState;
    });
  }, []);

  const onChange = useCallback(
    (prop) => (event) => {
      setValidationErrors((prev) => prev?.filter((e) => e.path !== prop));

      let { value, type } = event.target;
      if (type === 'checkbox') {
        value = event.target.checked;
      }

      setValue(prop, value);
    },
    []
  );

  const validate = useCallback((event) => {
    if (!event) return false;

    event.preventDefault();
    const htmlForm = event.currentTarget;

    if (
      htmlForm instanceof HTMLFormElement &&
      htmlForm?.checkValidity() === false
    ) {
      event.stopPropagation();
      setValidated(true);

      scrollIntoFirstInvalidItem(htmlForm);
      return false;
    }

    return true;
  }, []);

  const reset = useCallback(() => setValues(initialValues), []);

  const getValidity = useCallback((property) => {
    const hasErrors = validationErrors?.some((e) => e.path === property);

    return {
      isValid: validationErrors == null ? false : !hasErrors,
      isInvalid: !!hasErrors
    };
  }, [validationErrors]);

  const getValidationErrors = useCallback((property) => {
    return validationErrors?.filter((e) => e.path === property);
  }, [validationErrors]);

  const input = useCallback(
    (property, { controlId = null, autoFocus = false } = {}) => {
      return {
        controlId: controlId || property,
        value: getNestedValue(property, values),
        onChange: onChange(property),
        ref: autoFocus ? focusRef : null,
        ...getValidity(property)
      };
    },
    [values, getValidity]
  );

  const select = useCallback(
    (
      property,
      {
        controlId = null,
        options = [],
        optionLabel = (opt) => opt.label,
        optionValue = (opt) => opt.id
      } = {}
    ) => {
      return {
        controlId: controlId || property,
        options: options || [],
        getOptionLabel: optionLabel,
        getOptionValue: optionValue,
        onChange: (opt) => setValue(property, opt),
        value: getNestedValue(property, values),
        ...getValidity(property)
      };
    },
    [values, getValidity]
  );

  const getValue = useCallback(
    (prop) => getNestedValue(prop, values),
    [values]
  );

  return {
    ref: formRef,
    values,
    validated,
    focusRef,
    setValue,
    setValues,
    onChange,
    setValidated,
    setValidationErrors,
    validate,
    getValidity,
    getValidationErrors,
    reset,
    input,
    select,
    getValue
  };
}